import DOMPurify from "dompurify";
import { Copy, Send, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SocialPublish } from "@/components/SocialPublish";
import { Field } from "@/components/articulo/Field";

const NETS = [
  ["facebook", "Facebook", 400],
  ["instagram", "Instagram", 2200],
  ["threads", "Threads", 500],
  ["x", "X", 280],
  ["linkedin", "LinkedIn", 700],
];

export const RedesTab = ({
  form, set, setSocial, copy, sendAt, setSendAt, broadcast, busy,
  articleId, outlets, outletId, imageUrl,
}) => (
  <>
    <div className="kicker mb-3">Publicaciones para redes sociales</div>
    {NETS.map(([net, label, limit]) => {
      const value = form.social?.[net] || "";
      return (
        <div key={net} className="mb-5">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider">{label}</Label>
            <div className="flex items-center gap-2">
              <span className={`text-xs mono ${value.length > limit ? "text-destructive" : "text-muted-foreground"}`}>
                {value.length}/{limit}
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => copy(`${value}\n\n${(form.hashtags || []).join(" ")}`.trim(), label)}
                data-testid={`copy-${net}-btn`}
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" />Copiar
              </Button>
            </div>
          </div>
          <Textarea
            rows={3}
            value={value}
            onChange={(e) => setSocial(net, e.target.value)}
            className="mt-1.5"
            data-testid={`social-${net}-input`}
          />
        </div>
      );
    })}
    <Field label="Mensaje de WhatsApp">
      <Textarea rows={4} value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1.5" data-testid="whatsapp-input" />
    </Field>
    <Field label="Mensaje de Telegram">
      <Textarea rows={4} value={form.telegram || ""} onChange={(e) => set("telegram", e.target.value)} className="mt-1.5" data-testid="telegram-input" />
    </Field>
    <div className="border-t border-border pt-4 mt-2">
      <Field label="Programar envío (vacío = inmediato)">
        <Input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} className="mt-1.5" data-testid="send-at-input" />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => broadcast(["telegram"])} disabled={busy === "bc"} data-testid="send-telegram-btn">
          <Send className="h-4 w-4 mr-2" />Telegram
        </Button>
        <Button variant="outline" onClick={() => broadcast(["whatsapp"])} disabled={busy === "bc"} data-testid="send-whatsapp-btn">
          <Send className="h-4 w-4 mr-2" />WhatsApp
        </Button>
        <Button onClick={() => broadcast(["telegram", "whatsapp"])} disabled={busy === "bc"} data-testid="send-both-btn">
          <Share2 className="h-4 w-4 mr-2" />Ambos canales
        </Button>
      </div>
    </div>
    <SocialPublish
      articleId={articleId}
      outlets={outlets}
      outletId={outletId}
      imageUrl={imageUrl}
      published={form.social_publications}
    />
  </>
);

export const VistaPrevia = ({ form }) => (
  <>
    {form.featured_image && (
      <>
        <img src={form.featured_image} alt="" className="w-full h-64 object-cover mb-2 rounded-sm" />
        {(form.captions || {})[form.featured_image] && (
          <p className="text-xs text-muted-foreground mb-4">
            {form.captions[form.featured_image]}
          </p>
        )}
      </>
    )}
    <div className="kicker text-primary">{form.category}</div>
    <h2 className="text-3xl font-bold leading-tight mt-2">{form.title}</h2>
    {form.subtitle && (
      <p className="serif text-xl text-muted-foreground italic mt-1.5" data-testid="preview-subtitle">
        {form.subtitle}
      </p>
    )}
    <p className="text-base text-muted-foreground mt-2 mb-6">{form.dek}</p>
    <div className="article-body text-base"
         dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.body_html || "") }} />
  </>
);

export const ArticleAside = ({ form, set }) => (
  <aside className="space-y-4 min-w-0">
    <div className="border border-border bg-card p-5">
      <div className="kicker mb-3">Imagen principal</div>
      {form.featured_image ? (
        <img src={form.featured_image} alt="" className="w-full h-36 object-cover rounded-sm mb-3" />
      ) : (
        <p className="text-sm text-muted-foreground mb-3">Sin imagen seleccionada.</p>
      )}
      <Input
        value={form.featured_image || ""}
        onChange={(e) => set("featured_image", e.target.value)}
        placeholder="URL de la imagen"
        data-testid="featured-image-input"
      />
      <p className="text-xs text-muted-foreground mt-2">Modo galería: {form.gallery_mode}</p>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {(form.images || []).map((img, i) => (
          <button
            key={img}
            onClick={() => set("featured_image", img)}
            className="border border-border hover:border-primary transition-colors"
            data-testid={`select-image-${i}`}
          >
            <img src={img} alt="" className="h-16 w-full object-cover" />
          </button>
        ))}
      </div>
    </div>

    <div className="border border-border bg-card p-5">
      <div className="kicker mb-3">Ficha técnica</div>
      <dl className="text-sm space-y-2">
        <div className="flex justify-between"><dt className="text-muted-foreground">Modelo</dt><dd className="mono text-xs">{form.model}</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">Lectura</dt><dd>{form.read_time} min</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">Autor</dt><dd className="truncate max-w-[140px]">{form.created_by}</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">CTA</dt><dd className="text-right text-xs max-w-[150px]">{form.cta}</dd></div>
      </dl>
      {form.source_url && (
        <a href={form.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary mt-3 block truncate">
          Fuente original
        </a>
      )}
    </div>

    {form.publications?.length > 0 && (
      <div className="border border-border bg-card p-5" data-testid="publications-panel">
        <div className="kicker mb-3">Publicaciones</div>
        {form.publications.map((p) => (
          <div key={`${p.outlet}-${p.post_id}`} className="mb-2">
            <a href={p.link} target="_blank" rel="noreferrer" className="block text-sm text-primary truncate">
              {p.outlet} · #{p.post_id}
            </a>
            <span className="text-xs text-muted-foreground">
              {p.subtitle_applied ? "Subtítulo y categoría principal aplicados en JNews" : "Publicado sin subtítulo"}
            </span>
          </div>
        ))}
      </div>
    )}

    {form.hashtags?.length > 0 && (
      <div className="border border-border bg-card p-5">
        <div className="kicker mb-3">Hashtags</div>
        <div className="flex flex-wrap gap-1.5">
          {form.hashtags.map((h, i) => <Badge key={`${h}-${i}`} variant="secondary">{h}</Badge>)}
        </div>
      </div>
    )}
  </aside>
);
